import { useState, useEffect } from "react";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Button } from "@/components/ui/Button";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { RotateCcw } from "lucide-react";

export function PercentageCalc() {
  const [a, setA] = useState("15");
  const [b, setB] = useState("250");
  const [res, setRes] = useState("");

  const calculate = (valA = a, valB = b) => {
    const numA = Number(valA);
    const numB = Number(valB);
    if (isNaN(numA) || isNaN(numB)) {
      setRes("Invalid inputs");
      return;
    }
    setRes(`${numA}% of ${numB} is ${((numA / 100) * numB).toFixed(2)}`);
  };

  useEffect(() => {
    calculate();
  }, []);

  return (
    <ToolPageLayout
      title="Percentage Calculator Online - Find X Percent of Y"
      description="Calculate percentages instantly. Find out what X% of Y is, or find ratios and proportions offline and in-browser with zero fees."
      h1="Percentage Calculator"
      category="Math & Numbers"
      categoryPath="/number-tools"
      howToUse={[
        "Type your target percentage value (X).",
        "Type your total parent value (Y).",
        "See the computed proportional portion value instantly."
      ]}
      faq={[
        { question: "How is a percentage calculated?", answer: "X% of Y is calculated by dividing X by 100 and multiplying the result by Y." },
        { question: "Can I enter decimal numbers?", answer: "Yes, our calculator supports decimals cleanly." }
      ]}
    >
      <div className="space-y-4 max-w-md mx-auto bg-white border border-slate-200 p-6 rounded-2xl shadow-xs">
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-slate-700">Calculate Ratio</Label>
          <div className="flex flex-wrap items-center gap-3">
            <Input value={a} onChange={e => { setA(e.target.value); calculate(e.target.value, b); }} className="w-20 h-10 text-center" /> 
            <span className="text-slate-500 font-bold">% of</span> 
            <Input value={b} onChange={e => { setB(e.target.value); calculate(a, e.target.value); }} className="w-28 h-10 text-center" />
            <Button onClick={() => calculate()} className="bg-slate-900 hover:bg-slate-800 text-white font-medium h-10">Calc</Button>
          </div>
        </div>
        {res && (
          <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-xl text-center mt-4">
            <span className="text-xs text-indigo-500 uppercase font-bold tracking-wider block">Computed Portion</span>
            <span className="text-lg font-extrabold text-indigo-900">{res}</span>
          </div>
        )}
      </div>
    </ToolPageLayout>
  );
}

export function BaseConverter() {
  const [num, setNum] = useState("255");
  const [baseFrom, setBaseFrom] = useState(10);
  const [baseTo, setBaseTo] = useState(16);
  const [res, setRes] = useState("");

  const calc = (valNum = num, valFrom = baseFrom, valTo = baseTo) => {
    try {
      if (!valNum.trim()) { setRes(""); return; }
      const parsed = parseInt(valNum, valFrom);
      if (isNaN(parsed)) throw new Error("Invalid number for source base");
      setRes(parsed.toString(valTo).toUpperCase());
    } catch(e:any) {
      setRes("Error: " + e.message);
    }
  };

  useEffect(() => {
    calc();
  }, []);

  return (
    <ToolPageLayout
      title="Number Base Converter Online - Decimal, Hex, Binary & Octal"
      description="Convert numbers between base-2 (binary), base-8 (octal), base-10 (decimal), and base-16 (hexadecimal) or custom bases instantly."
      h1="Number Base Converter"
      category="Math & Numbers"
      categoryPath="/number-tools"
      howToUse={[
        "Enter your starting numeral string.",
        "Set the starting radix base (From Base, e.g., 10 for Decimal).",
        "Set the target radix base (To Base, e.g., 16 for Hexadecimal).",
        "The translated numeral will appear immediately below."
      ]}
      faq={[
        { question: "What is base-16?", answer: "Base-16 (hexadecimal) uses digits 0-9 and letters A-F to represent numbers." },
        { question: "Are custom bases allowed?", answer: "Yes, you can specify custom radix values between 2 and 36." }
      ]}
    >
      <div className="space-y-4 max-w-xl mx-auto bg-white border border-slate-200 p-6 rounded-2xl shadow-xs">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
          <div className="flex-1 space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Number Input</Label>
            <Input value={num} onChange={e => { setNum(e.target.value); calc(e.target.value, baseFrom, baseTo); }} className="font-mono text-sm h-10" />
          </div>
          <div className="w-full sm:w-28 space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">From Base</Label>
            <Input type="number" value={baseFrom} onChange={e => { const val = Number(e.target.value) || 10; setBaseFrom(val); calc(num, val, baseTo); }} className="h-10" />
          </div>
          <div className="w-full sm:w-28 space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">To Base</Label>
            <Input type="number" value={baseTo} onChange={e => { const val = Number(e.target.value) || 16; setBaseTo(val); calc(num, baseFrom, val); }} className="h-10" />
          </div>
        </div>
        <Button onClick={() => calc()} className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium h-10 mt-2">Convert Base</Button>
        {res && (
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-center font-mono font-extrabold text-base text-slate-800 mt-4 break-all">
            {res.startsWith("Error") ? <span className="text-red-600">{res}</span> : `Result: ${res}`}
          </div>
        )}
      </div>
    </ToolPageLayout>
  );
}

export function UnitConverter() {
  const [val, setVal] = useState("100");
  const [unit, setUnit] = useState("cm");
  const [res, setRes] = useState("");

  const convert = (valInput = val, valUnit = unit) => {
    const v = Number(valInput);
    if (isNaN(v)) { setRes("Invalid numeric input"); return; }
    if(valUnit === "cm") setRes(`${v} cm = ${(v / 2.54).toFixed(3)} inches`);
    if(valUnit === "kg") setRes(`${v} kg = ${(v * 2.20462).toFixed(3)} lbs`);
    if(valUnit === "c") setRes(`${v}°C = ${((v * 9/5) + 32).toFixed(2)}°F`);
  };

  useEffect(() => {
    convert();
  }, []);

  return (
    <ToolPageLayout
      title="Unit Converter Online - Convert Imperial and Metric Sizes"
      description="Translate physical measurements from metric units (cm, kg, Celsius) to imperial standards (inches, lbs, Fahrenheit) instantly."
      h1="Unit Converter"
      category="Math & Numbers"
      categoryPath="/number-tools"
      howToUse={[
        "Type your measurement magnitude value.",
        "Select the targeted conversion dimension ratio (e.g., cm to inches).",
        "Read the converted unit value instantly in the console."
      ]}
      faq={[
        { question: "What dimensions are supported?", answer: "This tool supports standard length (cm to inches), weight (kg to pounds), and temperature (Celsius to Fahrenheit) conversions." },
        { question: "How precise is the conversion?", answer: "Outputs are rounded to three decimal points for precise engineering use." }
      ]}
    >
      <div className="space-y-4 max-w-md mx-auto bg-white border border-slate-200 p-6 rounded-2xl shadow-xs">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
          <div className="flex-1 space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Magnitude Value</Label>
            <Input type="number" value={val} onChange={e => { setVal(e.target.value); convert(e.target.value, unit); }} className="h-10" />
          </div>
          <div className="w-full sm:w-36 space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Unit Dimension</Label>
            <select value={unit} onChange={e => { setUnit(e.target.value); convert(val, e.target.value); }} className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
              <option value="cm">cm to inches</option>
              <option value="kg">kg to lbs</option>
              <option value="c">C to F</option>
            </select>
          </div>
        </div>
        <Button onClick={() => convert()} className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium h-10 mt-2">Convert Value</Button>
        {res && (
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-center font-bold text-slate-800 mt-4">
            {res}
          </div>
        )}
      </div>
    </ToolPageLayout>
  );
}

export function NumberTools() {
  return (
    <ToolPageLayout
      title="Percentage & Conversion Tools"
      description="Various calculators: Percentage calculation, Base conversion, and Unit conversion."
      h1="Percentage & Conversion Tools"
      category="Number Tools"
      categoryPath="/number-tools"
      howToUse={[
        "Select the tool you need.",
        "Enter your values.",
        "See the results."
      ]}
      faq={[
        { question: "Are these tools free?", answer: "Yes, all our number tools are free." }
      ]}
    >
      <div className="space-y-12">
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">Percentage Calculator</h2><PercentageCalc /></section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">Base Converter</h2><BaseConverter /></section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">Unit Converter</h2><UnitConverter /></section>
      </div>
    </ToolPageLayout>
  );
}
